import { numberFormat } from "../../../shared/CommonFuctions";

export const LV_COLORS = {
  primary: "#252B88",
  background: "#F5F7FB",
  card: "#FFFFFF",
  border: "#E6EAF2",
  text: "#1F2937",
  textSecondary: "#6B7280",
  debit: "#C62828",
  credit: "#2E7D32",
  openingBg: "#e3e4e6",
  openingMobileBg: "#E8F5E9",
  selectedRow: "#F1F6FE",
  badgeBlue: "#E8EEFC",
  badgeBlueText: "#252B88",
  badgeOrange: "#FFF3E0",
  badgeOrangeText: "#E65100",
  badgeGreen: "#E8F5E9",
  badgeGreenText: "#2E7D32",
  badgeRed: "#FFEBEE",
  badgeRedText: "#C62828",
  badgeGray: "#F3F4F6",
  badgeGrayText: "#4B5563",
};

export function parseAccountTitle(accountName?: string): {
  title: string;
  subtitle: string;
} {
  const raw = (accountName || "").trim();
  if (!raw) {
    return { title: "", subtitle: "" };
  }
  const match = raw.match(/^(.*?)\s*(\([^)]*\))\s*$/);
  if (match) {
    return { title: match[1].trim(), subtitle: match[2].trim() };
  }
  return { title: raw, subtitle: "" };
}

export function getVoucherTypeBadgeColors(voucherType?: string): {
  bg: string;
  color: string;
} {
  const type = (voucherType || "").toLowerCase();
  // Short codes: PAY/PUR/EXP VCH | JRN/CON | REC/SAL/INC VCH/CRN | DBN
  if (
    type.startsWith("pay") ||
    type.startsWith("pur") ||
    type.includes("exp") ||
    type.includes("payment") ||
    type.includes("purchase") ||
    type.includes("expense")
  ) {
    return { bg: LV_COLORS.badgeBlue, color: LV_COLORS.badgeBlueText };
  }
  if (
    type.startsWith("jrn") ||
    type.startsWith("con") ||
    type.includes("journal") ||
    type.includes("contra")
  ) {
    return { bg: LV_COLORS.badgeOrange, color: LV_COLORS.badgeOrangeText };
  }
  if (
    type.startsWith("rec") ||
    type.startsWith("sal") ||
    type.includes("inc") ||
    type.startsWith("crn") ||
    type.includes("receipt") ||
    type.includes("sale") ||
    type.includes("income") ||
    type.includes("credit")
  ) {
    return { bg: LV_COLORS.badgeGreen, color: LV_COLORS.badgeGreenText };
  }
  if (type.startsWith("dbn") || type.includes("debit")) {
    return { bg: LV_COLORS.badgeRed, color: LV_COLORS.badgeRedText };
  }
  return { bg: LV_COLORS.badgeGray, color: LV_COLORS.badgeGrayText };
}

export function formatAmount(value: any): string {
  if (value === null || value === undefined || value === "" || Number(value) === 0) {
    return "";
  }
  return numberFormat(value);
}

export function formatAmountWithSymbol(value: any): string {
  const formatted = formatAmount(value);
  return formatted ? `₹${formatted}` : "₹0.00";
}

export function getOpeningBalanceDisplay(total: any): {
  amount: string;
  type: "CR" | "DR" | "";
} {
  if (!total) {
    return { amount: "", type: "" };
  }
  if (Number(total.opningBalanceDebit) !== 0) {
    return {
      amount: formatAmountWithSymbol(total.opningBalanceDebit),
      type: "DR",
    };
  }
  if (Number(total.opningBalanceCredit) !== 0) {
    return {
      amount: formatAmountWithSymbol(total.opningBalanceCredit),
      type: "CR",
    };
  }
  return { amount: "", type: "" };
}

export function getBalanceDisplay(
  runningBalance: any,
  transactionType?: string
): { amount: string; type: "CR" | "DR" | "" } {
  const typeRaw = (transactionType || "").toString().trim().toUpperCase();
  const type = typeRaw === "CR" || typeRaw === "DR" ? (typeRaw as "CR" | "DR") : "";
  if (runningBalance === null || runningBalance === undefined || runningBalance === "") {
    return { amount: "", type };
  }
  return {
    amount: formatAmountWithSymbol(runningBalance),
    type,
  };
}


export function isAbnormalRunningBalance(
  accountNature?: string,
  transactionType?: string
): boolean {
  const nature = (accountNature || "").toString().trim().toUpperCase();
  const type = (transactionType || "").toString().trim().toUpperCase();
  if (
    (nature !== "DR" && nature !== "CR") ||
    (type !== "DR" && type !== "CR")
  ) {
    return false;
  }
  return nature !== type;
}

export function parseSaleLabels(saleLabels: any): Array<{ label: string; labelId?: number }> {
  try {
    return saleLabels ? JSON.parse(saleLabels) : [];
  } catch {
    return [];
  }
}

export type ParticularsEntry = {
  name: string;
  amount: string | null;
};

/**
 * Parses ledger Particulars into party + amount rows.
 * Supports:
 * 1) particularsJson: [{"name":"...","amount":1234.5}, ...]
 * 2) multiline text: "Party Name - 1,234.00"
 * Falls back to plain lines when amount is not present.
 */
export function parseParticularsEntries(
  particulars?: string | null,
  particularsJson?: string | null,
): ParticularsEntry[] {
  if (particularsJson) {
    try {
      const parsed = JSON.parse(particularsJson);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed
          .map((row: any) => {
            const name = String(row?.name ?? row?.Name ?? "").trim();
            if (!name) return null;
            const rawAmount = row?.amount ?? row?.Amount;
            const amount =
              rawAmount === null || rawAmount === undefined || rawAmount === ""
                ? null
                : numberFormat(rawAmount);
            return { name, amount } as ParticularsEntry;
          })
          .filter(Boolean) as ParticularsEntry[];
      }
    } catch {
      // fall through to text parse
    }
  }

  const text = (particulars || "").toString().replace(/\r\n/g, "\n").trim();
  if (!text) return [];

  return text.split("\n").map((line) => {
    const trimmed = line.trim();
    // Match "Party Name - 12,345.00" (amount at end)
    const match = trimmed.match(/^(.*)\s-\s(-?[\d,]+\.\d{2})$/);
    if (match) {
      return {
        name: match[1].trim(),
        amount: match[2],
      };
    }
    return { name: trimmed, amount: null };
  });
}
